import { zodResolver } from '@hookform/resolvers/zod';
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	useScanner,
	useValidationResultDialog
} from '@nest/uikit-react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useTerminalClient } from '../client-rpc-router';
import { StorageLocationScanInput } from '../components/StorageLocationScanInput';
import { TerminalScreen } from '../components/TerminalScreen';

const deliveryReturnFormSchema = z.object({
	invoiceNumber: z.string().min(1, 'Číslo faktury je povinné'),
	storageLocation: z
		.object({
			id: z.number()
		})
		.nullable()
		.refine(value => value != null, {
			message: 'Cílová lokace je povinná'
		})
});

type DeliveryReturnFormValues = z.infer<typeof deliveryReturnFormSchema>;

function InvoiceField({ autoFocus, onConfirm }: { autoFocus: boolean; onConfirm: () => void }) {
	const form = useFormContext<DeliveryReturnFormValues>();

	return (
		<FormField
			control={form.control}
			name="invoiceNumber"
			render={({ field }) => (
				<FormItem>
					<FormLabel>Číslo faktury</FormLabel>
					<div className="flex gap-2">
						<FormControl>
							<Input
								{...field}
								placeholder="Naskenujte nebo zadejte číslo faktury"
								autoFocus={autoFocus}
								onKeyDown={event => {
									if (event.key === 'Enter') {
										event.preventDefault();
										const current = form.getValues('invoiceNumber');
										if (current) {
											onConfirm();
										} else {
											void form.trigger('invoiceNumber');
										}
									}
								}}
							/>
						</FormControl>
					</div>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}

export default function DeliveryNewReturnPage() {
	const client = useTerminalClient();
	const scanner = useScanner();
	const [activeTab, setActiveTab] = useState<'invoice' | 'storageLocation'>('invoice');

	const form = useForm<DeliveryReturnFormValues>({
		resolver: zodResolver(deliveryReturnFormSchema),
		defaultValues: {
			invoiceNumber: '',
			storageLocation: null
		}
	});

	const createDeliveryReturnMutation = useMutation({
		mutationFn: (input: DeliveryReturnFormValues) =>
			client.shipments.createDeliveryReturnMutation.execute({
				invoiceNumber: input.invoiceNumber,
				storageLocation: input.storageLocation!
			}),
		onSuccess: () => {
			toast.success('Vrácení bylo vytvořeno');
			form.reset({ invoiceNumber: '', storageLocation: null });
			setActiveTab('invoice');
		}
	});

	useValidationResultDialog([createDeliveryReturnMutation.error]);

	useEffect(() => {
		if (!createDeliveryReturnMutation.isPending && scanner.code && activeTab === 'invoice') {
			form.setValue('invoiceNumber', scanner.code);
			form.setValue('storageLocation', null);
			setActiveTab('storageLocation');
		}
	}, [scanner.code, form, createDeliveryReturnMutation.isPending, activeTab]);

	const handleSubmit = form.handleSubmit(values => {
		createDeliveryReturnMutation.mutate(values);
	});

	return (
		<TerminalScreen
			showBackButton
			topBar={<div>Vrácení rozvozu</div>}
			bottomContent={
				<div className="flex flex-col gap-2">
					<Button type="button" onClick={handleSubmit} disabled={createDeliveryReturnMutation.isPending} loading={createDeliveryReturnMutation.isPending}>
						Potvrdit vrácení
					</Button>
				</div>
			}
		>
			<Form {...form}>
				<div className="p-4 flex flex-col gap-4">
					<Tabs value={activeTab} onValueChange={v => setActiveTab(v as 'invoice' | 'storageLocation')} className="w-full">
						<TabsList className="grid grid-cols-2 w-full">
							<TabsTrigger value="invoice">Faktura</TabsTrigger>
							<TabsTrigger value="storageLocation">Lokace</TabsTrigger>
						</TabsList>

						<TabsContent value="invoice" className="space-y-4 p-2">
							<InvoiceField
								autoFocus={activeTab === 'invoice'}
								onConfirm={() => {
									const current = form.getValues('invoiceNumber');
									if (current) {
										form.setValue('storageLocation', null);
										setActiveTab('storageLocation');
									} else {
										void form.trigger('invoiceNumber');
									}
								}}
							/>
						</TabsContent>

						<TabsContent value="storageLocation" className="space-y-4 p-2">
							<FormField
								control={form.control}
								name="storageLocation"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Cílová lokace</FormLabel>
										<FormControl>
											<StorageLocationScanInput
												value={field.value}
												onChange={value => {
													form.setValue('storageLocation', value);
												}}
												autoFocus={activeTab === 'storageLocation'}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</TabsContent>
					</Tabs>
				</div>
			</Form>
		</TerminalScreen>
	);
}
